export enum ApiFunctionalErrorCode {
    B2CUserNotAllowed = "B2CUserNotAllowedFunctionalError",
}

export class ApiFunctionalError extends Error {
    public readonly status: number;

    public readonly code: string;

    public constructor(
        message: string,
        status: number,
        code: string,
        options?: { cause?: unknown },
    ) {
        super(message);
        this.name = "ApiFunctionalError";
        this.status = status;
        this.code = code;
        if (options?.cause !== undefined) {
            (this as { cause?: unknown }).cause = options.cause;
        }
        Object.setPrototypeOf(this, ApiFunctionalError.prototype);
    }
}

/**
 * Réponse 426 de l'Api-Backend : cette version de l'app n'est plus acceptée et doit être mise à
 * jour (F-1.25, BK-5). Hérite d'`ApiFunctionalError` pour traverser `callApi` sans être remplacée
 * par un simple texte ; `callApi` émet en plus l'événement `client-update-required`.
 */
export class ClientUpdateRequiredError extends ApiFunctionalError {
    public static readonly STATUS = 426;

    /** Code retenu quand la réponse n'en donne pas. */
    public static readonly DEFAULT_CODE = "UPDATE_REQUIRED";

    public constructor(message: string, code?: string) {
        super(
            message,
            ClientUpdateRequiredError.STATUS,
            code || ClientUpdateRequiredError.DEFAULT_CODE,
        );
        this.name = "ClientUpdateRequiredError";
        // Le constructeur parent fixe son propre prototype : on rétablit celui de la sous-classe.
        Object.setPrototypeOf(this, ClientUpdateRequiredError.prototype);
    }
}

/**
 * Échec de `fetch` lui-même (réseau coupé, serveur injoignable) : aucune réponse HTTP. Posée par
 * `ApiManager._sendRequest` pour que la cause d'un tour raté de l'assistant (F-1.17) reste lisible
 * sous les enveloppes successives (`callApi`, puis la méthode appelante).
 */
export class ApiNetworkError extends Error {
    public constructor(message: string, options?: { cause?: unknown }) {
        super(message);
        this.name = "ApiNetworkError";
        if (options?.cause !== undefined) {
            (this as { cause?: unknown }).cause = options.cause;
        }
        Object.setPrototypeOf(this, ApiNetworkError.prototype);
    }
}

/**
 * `new Error(message, { cause })` sans dépendre du support de l'option `cause` (ES2022) par le
 * moteur JS de l'app : la cause est posée à la main, comme dans `ApiFunctionalError`.
 */
export const errorWithCause = (message: string, cause: unknown): Error => {
    const error = new Error(message);
    (error as { cause?: unknown }).cause = cause;
    return error;
};

export const isApiFunctionalError = (
    error: unknown,
    code: ApiFunctionalErrorCode,
): boolean => error instanceof ApiFunctionalError && error.code === code;

export const isB2CUserNotAllowedError = (error: unknown): boolean =>
    isApiFunctionalError(error, ApiFunctionalErrorCode.B2CUserNotAllowed);

export const isNonRetryableApiError = (error: unknown): boolean =>
    error instanceof ApiFunctionalError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 429;
